/**
 * AttributeHistoryView — the per-attribute value-history view of the
 * Verlauf panel (#1913 C). Fetches the value series for ONE attribute
 * and renders it two ways: a numeric attribute becomes a small recharts
 * line (``AttributeSeriesChart``), a categorical one a dated value list.
 *
 * Both paths surface the WRITE-TIME provenance (source doc → year) the
 * pinned default asks for — a "Beleg 2024" chip that previews the
 * document via the existing ``onPreviewDocument`` thread (never a raw
 * id). It is mounted only while an attribute is selected, so its
 * ``useEntityAttributeSeries`` hook runs unconditionally within the
 * child (no conditional-hook hazard in the parent log).
 */
import React from 'react';
import { FileText } from 'lucide-react';
import type { EntitySourceDoc, LenseField } from '../../../services/twinService';
import { useEntityAttributeSeries } from '../../../hooks/useTwin';
import { formatDateString, formatScalar } from '../formatValue';
import AttributeSeriesChart from './AttributeSeriesChart';
import {
  attributeLabel,
  formatSeriesValue,
  isNumericSeries,
  provenanceLabel,
  seriesChartData,
  seriesPoints,
} from './attributeHistory';

export interface AttributeHistoryViewProps {
  entityType: string;
  entityId: string;
  attribute: string;
  field?: LenseField;
  /** This attribute's value labels from the card's projection (HOF-696 D20). */
  valueLabels?: Record<string, string>;
  /** Linked source docs by id — resolves the "Beleg <year>" provenance. */
  docsById?: Map<string, EntitySourceDoc>;
  onPreviewDocument?: (docId: string) => void;
}

/** The provenance chip for a categorical row: a preview link, or plain text. */
function ProvenanceChip({
  sourceId,
  docsById,
  onPreviewDocument,
}: {
  sourceId: string | null;
  docsById?: Map<string, EntitySourceDoc>;
  onPreviewDocument?: (docId: string) => void;
}) {
  const label = provenanceLabel(sourceId, docsById);
  if (!label || !sourceId) return null;
  if (!onPreviewDocument) return <span className="attr-history__prov">{label}</span>;
  return (
    <button type="button" className="attr-history__prov-link" onClick={() => onPreviewDocument(sourceId)}>
      <FileText size={12} />
      {label}
    </button>
  );
}

export const AttributeHistoryView: React.FC<AttributeHistoryViewProps> = ({
  entityType,
  entityId,
  attribute,
  field,
  valueLabels,
  docsById,
  onPreviewDocument,
}) => {
  const { data, isLoading, isError } = useEntityAttributeSeries(entityType, entityId, attribute);
  const label = attributeLabel(attribute, field);

  if (isLoading) {
    return <p className="attr-history__status">Werteverlauf wird geladen…</p>;
  }
  if (isError) {
    return <p className="attr-history__status attr-history__status--error">Werteverlauf konnte nicht geladen werden.</p>;
  }

  // Defensive time-ascending sort — the series contract returns groups
  // ascending, but don't trust envelope order for correctness (the sibling
  // log sorts too): the chart needs ascending x, the list newest-first.
  const points = [...seriesPoints(data?.groups ?? [])].sort((a, b) => a.t.localeCompare(b.t));
  if (points.length === 0) {
    return <p className="attr-history__status">Kein Werteverlauf vorhanden.</p>;
  }

  // ``truncated`` = the backend capped the series (newest kept). Surface it
  // so a partial chart/list is never read as the full history.
  const truncated = Boolean(data?.truncated);
  const truncationHint = truncated ? (
    <p className="attr-history__truncated">Nur die neuesten Werte werden angezeigt.</p>
  ) : null;

  if (isNumericSeries(points, field)) {
    return (
      <>
        {truncationHint}
        <AttributeSeriesChart
          data={seriesChartData(points, docsById)}
          label={label}
          valueFormatter={(v) => formatScalar(v, attribute, field)}
        />
      </>
    );
  }

  // Categorical: a dated value list, newest-first (matches the log order).
  const dated = [...points].reverse();
  return (
    <>
      {truncationHint}
      <ul className="attr-history__list" aria-label={`Werteverlauf: ${label}`}>
        {dated.map((p, i) => (
          <li key={`${p.t}-${i}`} className="attr-history__row">
            <span className="attr-history__date">{formatDateString(p.t)}</span>
            <span className="attr-history__value">{formatSeriesValue(p.value, p.absent, attribute, field, valueLabels)}</span>
            <ProvenanceChip sourceId={p.sourceId} docsById={docsById} onPreviewDocument={onPreviewDocument} />
          </li>
        ))}
      </ul>
    </>
  );
};

export default AttributeHistoryView;
